import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-premium';
import { useContext, useEffect, useMemo, useState } from 'react';

import ReadOnlyAddress from '@/components/common/address_components/ReadOnlyAddress';
import { Spinner } from '@/components/common/Spinner';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useUser from '@/hooks/useUser';
import { AddressProps } from '@/models/Address';
import { signupMethodAsString, UserProps, UserType } from '@/models/User';
import { formatPhoneNumber } from '@/utils/phoneNumberUtil';

import ApplicantModal from './ApplicantModal';
import EditUserModal from './EditUserModal';
import UserImpersonateModal from './UserImpersonateModal';

function UserDataGridDetails({
  userRow,
  updateRow,
}: {
  userRow: GridRowParams<UserProps>;
  updateRow: (usersToUpdate: UserProps[]) => void;
}) {
  const theme = useTheme();
  const { b2cId } = useContext(TokenContext);
  const { usersAll, userCurrent, updateUserCurrent } = useContext(UsersContext);
  const { postUser, updateUsers } = useUser();

  const [selectedUser, setSelectedUser] = useState<UserProps | null>(null);

  const [editUserModalOpen, setEditUserModalOpen] = useState<boolean>(false);
  const [applicantModalOpen, setApplicantModalOpen] = useState<boolean>(false);

  const [userImpersonateModalOpen, setUserImpersonateModalOpen] = useState(false);
  const [applicantModalClosing, setApplicantModalClosing] = useState<boolean>(false);

  useEffect(() => {
    if (userRow) {
      setSelectedUser(userRow.row);
    }
  }, [userRow]);

  useEffect(() => {
    if (!applicantModalOpen && applicantModalClosing) {
      setApplicantModalClosing(false);
    }
  }, [applicantModalClosing, applicantModalOpen]);

  const selectedUserAddress: AddressProps = useMemo(() => {
    return {
      streetAddress1: selectedUser?.contact?.streetAddress1,
      streetAddress2: selectedUser?.contact?.streetAddress2,
      city: selectedUser?.contact?.city,
      stateId: selectedUser?.contact?.stateId,
      zipCode: selectedUser?.contact?.zipCode,
      countryId: selectedUser?.contact?.countryId,
      countyId: selectedUser?.contact?.countyId,
    } satisfies AddressProps;
  }, [selectedUser?.contact]);

  const userImpersonated: UserProps | null = useMemo(() => {
    if (!usersAll) return null;
    return usersAll.find((u) => u.b2CId == selectedUser?.userImpersonated) ?? null;
  }, [selectedUser?.userImpersonated, usersAll]);

  const userImpersonating: UserProps | null = useMemo(() => {
    if (!usersAll) return null;
    return usersAll.find((u) => u.b2CId == selectedUser?.userImpersonatedBy) ?? null;
  }, [selectedUser?.userImpersonatedBy, usersAll]);

  const impersonating = useMemo(() => {
    return userImpersonated != null && userImpersonating != null;
  }, [userImpersonated, userImpersonating]);

  const canImpersonateMessage = useMemo(() => {
    return impersonating
      ? 'User is currently impersonating another user. Stop impersonating to change this property.'
      : 'This switch gives a user permissions to impersonate another user.';
  }, [impersonating]);

  const userImpersonatingMessage = useMemo(() => {
    if (!impersonating || !userImpersonating || !userImpersonated)
      return selectedUser?.disabled
        ? 'You cannot impersonate a disabled user.'
        : 'Click to start impersonating.';

    return selectedUser?.userImpersonatedBy && impersonating
      ? `This user is being impersonated by ${userImpersonating.userEmail}. Click to stop this user from being impersonated.`
      : selectedUser?.userImpersonated && impersonating
        ? `This user is impersonating ${userImpersonated?.contact?.firstName} ${userImpersonated?.contact?.lastName} (${userImpersonated.email}). Click to stop this user from impersonating.`
        : 'Click to start impersonating.';
  }, [
    impersonating,
    userImpersonating,
    userImpersonated,
    selectedUser?.disabled,
    selectedUser?.userImpersonatedBy,
    selectedUser?.userImpersonated,
  ]);

  const handleUserImpersonateModalOpen = () => setUserImpersonateModalOpen(true);

  const handleEditUserModalOpen = () => setEditUserModalOpen(true);
  const handleApplicantModalOpen = () => setApplicantModalOpen(true);
  const handleUserEditModalClose = () => setEditUserModalOpen(false);

  const handleUserUpdate = async (updatedUser: UserProps) => {
    const user = await postUser(updatedUser);

    if (user.b2CId == b2cId) {
      updateUserCurrent(user);
    }

    updateRow([user]);
    updateUsers(user);
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const updatedUser = { ...selectedUser, [name]: checked };
    await handleUserUpdate(updatedUser as UserProps);
  };

  return selectedUser != null ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        borderBottom: `1px solid ${theme.palette.sandShade2.main}`,
        height: 'calc(100% - 24px)',
        overflowY: 'auto',
        padding: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          paddingBottom: '12px',
          borderBottom: `1px solid ${theme.palette.sandShade1.main}`,
        }}
      >
        {selectedUser.userType != UserType.Adf && (
          <Tooltip title={userImpersonatingMessage}>
            <span>
              <Button
                variant='secondary'
                size='small'
                onClick={() => {
                  handleUserImpersonateModalOpen();
                }}
                disabled={selectedUser.disabled}
              >
                Impersonate User
              </Button>
            </span>
          </Tooltip>
        )}

        {selectedUser.userType === UserType.Applicant && (
          <Button
            variant='secondary'
            size='small'
            onClick={() => {
              handleApplicantModalOpen();
            }}
          >
            Re-Assign User
          </Button>
        )}
        {userCurrent?.userType === UserType.Adf && (
          <Button
            variant='secondary'
            size='small'
            onClick={() => {
              handleEditUserModalOpen();
            }}
          >
            Edit User
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '64px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography variant='h6' display={'grid'}>
            Contact Information
          </Typography>

          <Box>
            <Typography variant='overline2'>Name</Typography>
            <Typography color='textSecondary'>
              {selectedUser.contact
                ? `${selectedUser.contact.firstName} ${selectedUser.contact.lastName}`
                : '---'}
            </Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Email</Typography>
            <Typography color='textSecondary'>{selectedUser.contact?.email ?? '---'}</Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Phone</Typography>
            <Typography color='textSecondary'>
              {selectedUser.contact?.phone ? formatPhoneNumber(selectedUser.contact.phone) : '---'}
            </Typography>
          </Box>

          <ReadOnlyAddress address={selectedUserAddress} programId={1} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography variant='h6' display={'grid'}>
            Manage User
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {selectedUser.userType === UserType.Applicant && (
              <FormControlLabel
                label={
                  <Box flexDirection={'row'} display={'flex'}>
                    <Typography>Admin</Typography>

                    <Tooltip title='This switch gives a user admin permissions.'>
                      <InfoOutlinedIcon sx={{ ml: 2 }} />
                    </Tooltip>
                  </Box>
                }
                control={
                  <Checkbox
                    id={`user-details-admin-checkbox-${selectedUser.b2CId}`}
                    checked={selectedUser.admin}
                    name={'admin'}
                    onChange={handleCheckboxChange}
                  />
                }
              />
            )}

            {selectedUser.userType === UserType.Applicant && (
              <FormControlLabel
                label={
                  <Box flexDirection={'row'} display={'flex'}>
                    <Typography>Validated</Typography>

                    <Tooltip title='This allows a user to view and submit applications.'>
                      <InfoOutlinedIcon sx={{ ml: 2 }} />
                    </Tooltip>
                  </Box>
                }
                control={
                  <Checkbox
                    id={`user-details-validated-checkbox-${selectedUser.b2CId}`}
                    disabled={selectedUser.validated} // disable if user is already validated
                    checked={selectedUser.validated}
                    name={'validated'}
                    onChange={handleCheckboxChange}
                  />
                }
              />
            )}

            <FormControlLabel
              label={
                <Box flexDirection={'row'} display={'flex'}>
                  <Typography>Disabled</Typography>

                  <Tooltip title="This switch de-activates a user's account.">
                    <InfoOutlinedIcon sx={{ ml: 2 }} />
                  </Tooltip>
                </Box>
              }
              control={
                <Checkbox
                  id={`user-details-disabled-checkbox-${selectedUser.b2CId}`}
                  name={'disabled'}
                  checked={selectedUser.disabled}
                  onChange={handleCheckboxChange}
                />
              }
            />

            {(selectedUser.userType === UserType.Adf ||
              selectedUser.userAccountType === UserType.Adf) && (
              <Tooltip title={canImpersonateMessage}>
                <FormControlLabel
                  label={'Allow Impersonation'}
                  control={
                    <Checkbox
                      disabled={impersonating ?? false}
                      id={`user-details-allow-impersonation-checkbox-${selectedUser.b2CId}`}
                      checked={selectedUser.userCanImpersonate}
                      name={'userCanImpersonate'}
                      onChange={handleCheckboxChange}
                    />
                  }
                />
              </Tooltip>
            )}

            <Box>
              <Typography variant='overline2'>Signup Method</Typography>
              <Typography color='textSecondary'>
                {signupMethodAsString(selectedUser.signupMethod)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <UserImpersonateModal
        userImpersonateModalOpen={userImpersonateModalOpen}
        targetUser={selectedUser}
        theme={theme}
        handleRowUpdate={updateRow}
        setUserImpersonateModalOpen={setUserImpersonateModalOpen}
      />

      {applicantModalOpen && (
        <ApplicantModal
          applicantModalOpen={applicantModalOpen}
          modalUser={selectedUser}
          theme={theme}
          setApplicantModalOpen={setApplicantModalOpen}
          setApplicantModalClosing={setApplicantModalClosing}
          handleRowUpdate={updateRow}
        />
      )}

      {editUserModalOpen && (
        <EditUserModal
          open={editUserModalOpen}
          onClose={handleUserEditModalClose}
          user={selectedUser}
          onSave={handleUserUpdate}
          saving={false}
        />
      )}
    </Box>
  ) : (
    <Box display={'flex'} mt={5} alignContent={'center'} justifyContent={'center'}>
      <Spinner size={100} type='circular' />
    </Box>
  );
}

export default UserDataGridDetails;
